// Ozy
// Shopify Chat Widget Dismiss Functionality
// Adds dismiss/restore capability to Shopify Inbox chat icon

(function() {
  'use strict';

  const STORAGE_KEY = 'shopify-chat-dismissed';
  const POSITION_KEY = 'shopify-chat-restore-position';
  
  // Multiple selectors to find Shopify Chat widget (they change their implementation)
  const CHAT_SELECTORS = [
    '#ShopifyChat',
    'shopify-chat',
    '#shopify-chat',
    '[class*="shopify-chat"]',
    'iframe[title*="chat" i]',
    'iframe[title*="Shopify" i]',
    '#chat-button',
    '.shopify-chat-button'
  ];
  
  let chatContainer = null;
  let dismissButton = null;
  let restoreButton = null;
  let hoverZone = null;
  let initialized = false;
  
  // Drag state
  let isDragging = false;
  let dragStartY = 0;
  let dragStartBottom = 0;
  let hasMoved = false;

  /**
   * Check if chat should be dismissed
   * Default: dismissed (docked) for new users
   * Only show if user explicitly restored it
   */
  function isDismissed() {
    const stored = localStorage.getItem(STORAGE_KEY);
    // Default to dismissed if no preference stored
    if (stored === null) {
      return true;
    }
    return stored === 'true';
  }

  /**
   * Save dismissed state to localStorage
   */
  function saveDismissedState(dismissed) {
    if (dismissed) {
      localStorage.setItem(STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * Save restore button position
   */
  function saveRestorePosition(bottom) {
    localStorage.setItem(POSITION_KEY, String(bottom));
  }

  /**
   * Get saved restore button position
   */
  function getSavedRestorePosition() {
    const saved = localStorage.getItem(POSITION_KEY);
    return saved ? parseInt(saved, 10) : null;
  }

  /**
   * Apply saved position to restore button
   */
  function applyRestorePosition() {
    const savedBottom = getSavedRestorePosition();
    if (savedBottom !== null && restoreButton) {
      restoreButton.style.bottom = savedBottom + 'px';
    }
  }

  /**
   * Find the Shopify Chat container using multiple selectors
   */
  function findChatContainer() {
    for (const selector of CHAT_SELECTORS) {
      const element = document.querySelector(selector);
      if (element) {
        // For iframes, get the parent container if it exists
        if (element.tagName === 'IFRAME' && element.parentElement) {
          return element.parentElement;
        }
        return element;
      }
    }
    return null;
  }

  /**
   * Create the dismiss X button overlay
   */
  function createDismissButton() {
    // Prevent duplicates
    if (document.getElementById('shopify-chat-dismiss-btn')) {
      dismissButton = document.getElementById('shopify-chat-dismiss-btn');
      return;
    }
    
    dismissButton = document.createElement('button');
    dismissButton.id = 'shopify-chat-dismiss-btn';
    dismissButton.className = 'shopify-chat-dismiss-btn';
    dismissButton.setAttribute('aria-label', 'Dismiss chat');
    dismissButton.setAttribute('title', 'Dismiss chat');
    dismissButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
    dismissButton.addEventListener('click', handleDismiss);
    document.body.appendChild(dismissButton);
  }

  /**
   * Create hover zone for better UX (larger hit area for hover reveal)
   */
  function createHoverZone() {
    // Prevent duplicates
    if (document.getElementById('shopify-chat-hover-zone')) {
      hoverZone = document.getElementById('shopify-chat-hover-zone');
      return;
    }
    
    hoverZone = document.createElement('div');
    hoverZone.id = 'shopify-chat-hover-zone';
    hoverZone.className = 'shopify-chat-hover-zone';
    
    // Use JavaScript events for hover since CSS sibling selector is unreliable
    hoverZone.addEventListener('mouseenter', showDismissButton);
    hoverZone.addEventListener('mouseleave', hideDismissButton);
    
    document.body.appendChild(hoverZone);
  }

  /**
   * Create the minimized restore button
   */
  function createRestoreButton() {
    // Prevent duplicates
    if (document.getElementById('shopify-chat-restore-btn')) {
      restoreButton = document.getElementById('shopify-chat-restore-btn');
      setupDragHandlers();
      return;
    }
    
    restoreButton = document.createElement('button');
    restoreButton.id = 'shopify-chat-restore-btn';
    restoreButton.className = 'shopify-chat-restore-btn';
    restoreButton.setAttribute('aria-label', 'Open chat (drag to reposition)');
    restoreButton.setAttribute('title', 'Open chat (drag to reposition)');
    restoreButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/><circle cx="8" cy="10" r="1.5"/><circle cx="12" cy="10" r="1.5"/><circle cx="16" cy="10" r="1.5"/></svg>';
    
    document.body.appendChild(restoreButton);
    setupDragHandlers();
  }

  /**
   * Setup drag handlers for restore button
   */
  function setupDragHandlers() {
    if (!restoreButton) return;
    
    // Touch events
    restoreButton.addEventListener('touchstart', handleDragStart, { passive: false });
    document.addEventListener('touchmove', handleDragMove, { passive: false });
    document.addEventListener('touchend', handleDragEnd);
    
    // Mouse events
    restoreButton.addEventListener('mousedown', handleDragStart);
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
  }

  /**
   * Handle drag start
   */
  function handleDragStart(e) {
    if (!restoreButton || !restoreButton.classList.contains('visible')) return;
    
    isDragging = true;
    hasMoved = false;
    
    // Get current bottom position
    const computedStyle = window.getComputedStyle(restoreButton);
    dragStartBottom = parseInt(computedStyle.bottom, 10) || 20;
    
    // Get start Y position
    if (e.type === 'touchstart') {
      dragStartY = e.touches[0].clientY;
    } else {
      dragStartY = e.clientY;
    }
    
    restoreButton.classList.add('dragging');
    
    // Prevent text selection during drag
    e.preventDefault();
  }

  /**
   * Handle drag move
   */
  function handleDragMove(e) {
    if (!isDragging || !restoreButton) return;
    
    let currentY;
    if (e.type === 'touchmove') {
      currentY = e.touches[0].clientY;
    } else {
      currentY = e.clientY;
    }
    
    const deltaY = dragStartY - currentY;
    
    // Only consider it a move if threshold exceeded
    if (Math.abs(deltaY) > 5) {
      hasMoved = true;
    }
    
    // Calculate new bottom position
    let newBottom = dragStartBottom + deltaY;
    
    // Constrain to viewport
    const buttonHeight = restoreButton.offsetHeight;
    const maxBottom = window.innerHeight - buttonHeight - 10;
    const minBottom = 10;
    
    newBottom = Math.max(minBottom, Math.min(maxBottom, newBottom));
    
    restoreButton.style.bottom = newBottom + 'px';
    
    e.preventDefault();
  }

  /**
   * Handle drag end
   */
  function handleDragEnd(e) {
    if (!isDragging) return;
    
    isDragging = false;
    
    if (restoreButton) {
      // Save position if actually moved
      if (hasMoved) {
        const computedStyle = window.getComputedStyle(restoreButton);
        const finalBottom = parseInt(computedStyle.bottom, 10);
        saveRestorePosition(finalBottom);
      }
      
      // Remove dragging class after a tiny delay to ensure smooth transition back
      // This fixes touch devices not tucking the button back in
      setTimeout(function() {
        if (restoreButton) {
          restoreButton.classList.remove('dragging');
        }
      }, 10);
      
      // If didn't move much, treat as click (restore chat)
      if (!hasMoved) {
        handleRestore(e);
      }
    }
    
    hasMoved = false;
  }

  /**
   * Show dismiss button on hover
   */
  function showDismissButton() {
    if (dismissButton && !isDismissed()) {
      dismissButton.classList.add('show');
    }
  }

  /**
   * Hide dismiss button when not hovering
   */
  function hideDismissButton() {
    if (dismissButton) {
      dismissButton.classList.remove('show');
    }
  }

  /**
   * Handle dismiss button click
   */
  function handleDismiss(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // Hide the chat widget using visibility to maintain layout
    if (chatContainer) {
      chatContainer.style.setProperty('visibility', 'hidden', 'important');
      chatContainer.style.setProperty('opacity', '0', 'important');
      chatContainer.style.setProperty('pointer-events', 'none', 'important');
    }
    
    // Hide dismiss button and hover zone
    if (dismissButton) {
      dismissButton.style.display = 'none';
    }
    if (hoverZone) {
      hoverZone.style.display = 'none';
    }
    
    // Show restore button
    if (restoreButton) {
      restoreButton.classList.add('visible');
    }
    
    saveDismissedState(true);
  }

  /**
   * Handle restore button click
   */
  function handleRestore(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Show the chat widget
    if (chatContainer) {
      chatContainer.style.removeProperty('visibility');
      chatContainer.style.removeProperty('opacity');
      chatContainer.style.removeProperty('pointer-events');
    }
    
    // Show dismiss button and hover zone
    if (dismissButton) {
      dismissButton.style.display = '';
    }
    if (hoverZone) {
      hoverZone.style.display = '';
    }
    
    // Hide restore button
    if (restoreButton) {
      restoreButton.classList.remove('visible');
    }
    
    saveDismissedState(false);
  }

  /**
   * Apply dismissed state if previously saved
   */
  function applyDismissedState() {
    if (isDismissed()) {
      if (chatContainer) {
        chatContainer.style.setProperty('visibility', 'hidden', 'important');
        chatContainer.style.setProperty('opacity', '0', 'important');
        chatContainer.style.setProperty('pointer-events', 'none', 'important');
      }
      if (dismissButton) {
        dismissButton.style.display = 'none';
      }
      if (hoverZone) {
        hoverZone.style.display = 'none';
      }
      if (restoreButton) {
        restoreButton.classList.add('visible');
      }
    }
  }

  /**
   * Initialize when chat widget is found
   */
  function initializeDismiss() {
    // Prevent double initialization
    if (initialized) {
      return true;
    }
    
    chatContainer = findChatContainer();
    
    if (!chatContainer) {
      return false;
    }
    
    initialized = true;

    // Create UI elements
    createHoverZone();
    createDismissButton();
    createRestoreButton();

    // Also add hover events to the dismiss button itself
    if (dismissButton) {
      dismissButton.addEventListener('mouseenter', showDismissButton);
      dismissButton.addEventListener('mouseleave', hideDismissButton);
    }

    // Apply saved position and state
    applyRestorePosition();
    applyDismissedState();

    return true;
  }

  /**
   * Watch for Shopify Chat widget to be added to DOM
   */
  function watchForChatWidget() {
    // Try immediately first
    if (initializeDismiss()) {
      return;
    }

    // Use MutationObserver to watch for the widget being added
    const observer = new MutationObserver(function(mutations) {
      if (initialized) {
        observer.disconnect();
        return;
      }
      
      if (initializeDismiss()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Fallback: retry a few times with delay
    let retries = 0;
    const maxRetries = 20;
    const retryInterval = setInterval(function() {
      retries++;
      if (initialized || initializeDismiss()) {
        clearInterval(retryInterval);
        observer.disconnect();
      } else if (retries >= maxRetries) {
        clearInterval(retryInterval);
        observer.disconnect();
      }
    }, 500);
  }

  // Start watching when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForChatWidget);
  } else {
    watchForChatWidget();
  }
})();
